import { Model, Attr, HasOne, HasMany } from 'spraypaint';
import { GeccoOrganisation, OrganisationUser } from '@/api';
import { OrganisationUserRole } from '@/api/models/OrganisationUser';
import Avatars from '@dicebear/avatars';
import sprites from '@dicebear/avatars-jdenticon-sprites';
import i18n from '@/plugins/vue-i18n';
import dayjs from 'dayjs';
import ApplicationRecord from '../ApplicationRecord';

export enum OrganisationType {
  'Regular' = 0,
  'TrainingProvider' = 1,
  'TrainingSession' = 2,
  'Trial' = 3,
  'None' = 4,
}

@Model()
export default class Organisation extends ApplicationRecord {
  static jsonapiType = 'organisations';

  static listHeaders() {
    return [
      {
        text: i18n.t('common.labels.id'),
        value: 'id',
        width: 70,
      },
      {
        text: i18n.t('common.labels.name'),
        value: 'name',
      },
      {
        text: i18n.t('organisations.labels.orgType'),
        value: 'orgType',
      },
      {
        text: i18n.t('organisations.access.labels.title'),
        value: 'vitalStatus',
      },
      {
        text: i18n.t('organisations.labels.training'),
        value: 'trainingEnds',
      },
    ];
  }

  static trainingListHeaders() {
    return [
      {
        text: i18n.t('common.labels.name'),
        value: 'name',
      },
      {
        text: i18n.t('organisations.labels.owner'),
        value: 'owner.fullName',
      },
      { text: i18n.t('common.labels.status'), value: 'trainingSessionStatus' },
      {
        text: i18n.t('organisations.labels.sessionBegins'),
        value: 'trainingBegins',
      },
      {
        text: i18n.t('common.labels.created'),
        value: 'created',
      },
      {
        value: 'actions',
        width: 64,
      },
    ];
  }

  static scopeFactory() {
    return Organisation.includes([]);
  }

  @Attr() geccoOrgId: number | null;

  @Attr() name: string;

  @Attr() description: string;

  @Attr() picture: string;

  @Attr() companyName: string;

  @Attr() created: string;

  @Attr() changed: string;

  @Attr() orgType: OrganisationType;

  @Attr() subscriptionRenewalDate: string;

  @Attr() subscriptionEndDate: string;

  @Attr() trainingBegins: string;

  @Attr() trainingExpires: string;

  @Attr() trainingActive: boolean;

  @Attr() sendWelcomeAt: string;

  @Attr() isActive: boolean;

  @Attr() manualVitalonlineAccess: boolean | null;

  // write only
  @Attr() owner: string;

  @HasOne() trainingProvider: Organisation;

  @Attr() isUpdating: boolean;

  @Attr() notes: string;

  // these are org users for the organisation
  @HasMany() userRelationships: OrganisationUser[];

  @Attr() readonly legislationCoreId: string;

  @Attr() defaultLegislation: string; // write only

  // Training session cloning - write only
  @HasOne() cloneSession: Organisation;

  setLatestOrgDetails(val: GeccoOrganisation | null) {
    if (!val) {
      return;
    }
    // cast to Organisation
    const geccoOrg = val as Organisation;
    // update this org
    this.name = geccoOrg.name;
    this.description = geccoOrg.description;
    this.companyName = geccoOrg.companyName;
    this.orgType = geccoOrg.orgType;
    this.subscriptionRenewalDate = geccoOrg.subscriptionRenewalDate;
    this.subscriptionEndDate = geccoOrg.subscriptionEndDate;
    this.trainingBegins = geccoOrg.trainingBegins;
    this.trainingExpires = geccoOrg.trainingExpires;
    this.trainingActive = geccoOrg.trainingActive;
    this.sendWelcomeAt = geccoOrg.sendWelcomeAt;
    this.isActive = geccoOrg.isActive;
    this.manualVitalonlineAccess = geccoOrg.manualVitalonlineAccess;
    this.isPersisted = true;
  }

  get avatar() {
    let avatar;
    if (this.picture) {
      avatar = this.picture;
    } else {
      const avatars = new Avatars(sprites, {
        dataUri: true,
        background: '#FFF',
      });
      avatar = avatars.create(`org${this.id}`);
    }
    return avatar;
  }

  get owners() {
    return this.userRelationships
      .filter(ou => ou.role === OrganisationUserRole.Owner)
      .map(ou => ou.user);
  }

  get managers() {
    return this.userRelationships
      .filter(ou => ou.role === OrganisationUserRole.Manager)
      .map(ou => ou.user);
  }

  get trainingSessionStatus() {
    if (dayjs().diff(dayjs(this.trainingBegins)) < 0) {
      return 'upcoming';
    }
    if (dayjs().diff(dayjs(this.trainingExpires)) < 0) {
      return 'active';
    }
    return 'expired';
  }

  get vitalStatus() {
    if (this.manualVitalonlineAccess === true) {
      return i18n.t('organisations.access.labels.activeManual');
    }
    if (this.manualVitalonlineAccess === false) {
      return i18n.t('organisations.access.labels.inactiveManual');
    }
    if (this.isActive) {
      return i18n.t('organisations.access.labels.active');
    }
    return i18n.t('organisations.access.labels.inactive');
  }

  get subscriptionExpired() {
    if (
      this.orgType !== OrganisationType.Regular ||
      !this.subscriptionEndDate
    ) {
      return false;
    }
    return dayjs().isAfter(dayjs(this.subscriptionEndDate));
  }
}
